import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import {
  useGetVersionFilesQuery,
  useSetCurrentVersionMutation,
} from '@/services/RoutingManagementService';
import { VersionFileMeta } from '@/services/RoutingManagementService';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayDate } from '@/utils/Date';

interface IVersionFile {
  id: number;
  version: number;
  filename: string;
  is_current: boolean;
  features_count: number;
  created_at?: string;
  notes?: string | null;
  version_name?: string;
}

interface CellProps {
  row: {
    original: IVersionFile;
  };
}

export const VersionListings: React.FC = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { hasPermission } = usePermissions();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    pagination,
    setPagination,
  } = usePagination();
  const [sourceVersionId, setSourceVersionId] = useState<number | null>(null);

  const queryParams = {
    page: pagination.pageIndex + 1,
    page_size: pagination.pageSize,
  };

  const {
    data: versionFilesResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetVersionFilesQuery(queryParams);

  const [setCurrentVersion] = useSetCurrentVersionMutation();

  useEffect(() => {
    if (versionFilesResponse) {
      handleTotalPages(versionFilesResponse.total_pages);
      handleTotalItems(versionFilesResponse.total_items);
    }
  }, [versionFilesResponse, handleTotalPages, handleTotalItems]);

  const versionFiles = useMemo(() => {
    if (
      !versionFilesResponse ||
      !Array.isArray(versionFilesResponse.versions)
    ) {
      return [];
    }
    return versionFilesResponse.versions.map((item: VersionFileMeta) => ({
      id: item.id,
      version: item.version,
      filename: item.merged_filename,
      is_current: item.is_current,
      features_count: item.features_count,
      created_at: item.created_at,
      notes: item.notes,
      version_name: item.version_name,
    }));
  }, [versionFilesResponse]);

  const columns = useMemo(
    () => [
      {
        header: intl.formatMessage({ id: 'ROUTING.VERSION' }),
        accessorKey: 'version',
        cell: ({ row }: CellProps) => (
          <div className="flex items-center gap-2">
            <div
              className="text-[14px] font-medium text-gray-900"
              data-testid="version-number"
            >
              {row.original.version_name}
            </div>
            {row.original.is_current && (
              <div
                className="text-xs text-green-600 truncate badge badge-[#0a7a27] bg-[#0a7a27] text-white px-1 py-0"
                data-testid="version-current"
              >
                {intl.formatMessage({ id: 'ROUTING.CURRENT' })}
              </div>
            )}
          </div>
        ),
        meta: { className: 'min-w-[170px]' },
      },
      {
        header: intl.formatMessage({ id: 'ROUTING.FILENAME' }),
        accessorKey: 'filename',
        cell: ({ row }: CellProps) => (
          <div
            className="text-[14px] text-gray-900 truncate"
            data-testid="version-filename"
          >
            {row.original.filename}
          </div>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        header: intl.formatMessage({ id: 'ROUTING.FEATURES_COUNT' }),
        accessorKey: 'features_count',
        cell: ({ row }: CellProps) => (
          <div
            className="text-[14px] text-gray-900"
            data-testid="version-features"
          >
            {row.original.features_count}{' '}
            {intl.formatMessage({ id: 'ROUTING.FEATURES' })}
          </div>
        ),
        meta: { className: 'min-w-[120px]' },
      },
      {
        header: intl.formatMessage({ id: 'ROUTING.CREATED_AT' }),
        accessorKey: 'created_at',
        cell: ({ row }: CellProps) => (
          <div className="text-[14px] text-gray-900" data-testid="version-date">
            {row.original.created_at
              ? formatDisplayDate(new Date(row.original.created_at))
              : '-'}
          </div>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        header: intl.formatMessage({ id: 'ROUTING.NOTES' }),
        accessorKey: 'notes',
        cell: ({ row }: CellProps) => (
          <div
            className="text-[14px] text-gray-900 truncate"
            data-testid="version-notes"
          >
            {row.original.notes || '-'}
          </div>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        header: intl.formatMessage({ id: 'ROUTING.ACTIONS' }),
        id: 'actions',
        enableSorting: false,
        cell: ({ row }: CellProps) => (
          <div className="flex items-center gap-3">
            {hasPermission('routing_management_read') && (
              <div className="relative group">
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-black border border-gray-600 rounded-full"
                  onClick={() => handleView(row.original.id)}
                  aria-label="View version file"
                >
                  <KeenIcon icon="eye" className="text-gray-700" />
                </button>
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max px-2 py-1 text-xs bg-gray-800 text-gray-100 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  {intl.formatMessage({ id: 'ROUTING.VIEW_VERSION' })}
                </span>
              </div>
            )}
            {!row.original.is_current &&
              hasPermission('routing_management_update') && (
                <div className="relative group">
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-black border border-gray-600 rounded-full"
                    onClick={() => handleSetCurrent(row.original)}
                    aria-label="Set as current version"
                  >
                    <KeenIcon icon="star" className="text-gray-700" />
                  </button>
                  <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max px-2 py-1 text-xs bg-gray-800 text-gray-100 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                    {intl.formatMessage({ id: 'ROUTING.SET_AS_CURRENT' })}
                  </span>
                </div>
              )}
            {hasPermission('routing_management_read') && (
              <div className="relative group">
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-black border border-gray-600 rounded-full"
                  onClick={() => handleSources(row.original.id)}
                  aria-label="View source files"
                >
                  <KeenIcon icon="data" className="text-gray-700" />
                </button>
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max px-2 py-1 text-xs bg-gray-800 text-gray-100 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  {intl.formatMessage({ id: 'ROUTING.VIEW_SOURCE_FILES' })}
                </span>
              </div>
            )}
          </div>
        ),
        meta: { className: 'w-[200px]' },
      },
    ],
    [intl]
  );

  const handleView = (versionId: number) => {
    navigate(`/routing-management/version-details/${versionId}`);
  };

  const handleSources = (versionId: number) => {
    setSourceVersionId(versionId);
  };

  useEffect(() => {
    if (sourceVersionId !== null) {
      navigate(`/routing-management/version-sources/${sourceVersionId}`);
      setSourceVersionId(null);
    }
  }, [sourceVersionId, navigate]);

  const handleSetCurrent = async (version: IVersionFile) => {
    try {
      await setCurrentVersion(version.id).unwrap();
      refetch();
    } catch (error) {
      console.error('Failed to set current version:', error);
    }
  };

  if (isError) {
    return (
      <div className="card">
        <div className="card-body">
          <p className="text-red-600">
            {intl.formatMessage({ id: 'ROUTING.ERROR_FETCHING_VERSION_FILES' })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card card-grid min-w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold">
            {intl.formatMessage({ id: 'ROUTING.VERSION_FILES' })}
          </h2>
          <span className="text-xs text-gray-600">
            {intl.formatMessage({ id: 'ROUTING.PUBLISHED_GEOJSON' })}
          </span>
        </div>
      </div>
      <div className="card-body">
        {isLoading || isFetching ? (
          <div className="text-center py-4" role="status" aria-live="polite">
            {intl.formatMessage({ id: 'ROUTING.LOADING_VERSION_FILES' })}
          </div>
        ) : isError ? (
          <div className="text-center py-4 text-red-500" role="alert">
            {intl.formatMessage({ id: 'ROUTING.FAILED_LOAD_VERSION_FILES' })}
          </div>
        ) : versionFiles.length === 0 ? (
          <div className="text-center py-4 text-gray-500" role="status">
            {intl.formatMessage({ id: 'ROUTING.NO_VERSION_FILES' })}
          </div>
        ) : (
          <DataGrid
            columns={columns}
            data={versionFiles}
            rowSelect={false}
            loading={isLoading || isFetching}
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              more: true,
              totalItems: totalItems,
              totalPages: totalPages,
              setPagination,
              info: `{from} - {to} of {count}`,
              sizes: [5, 10, 25, 50, 100],
              sizesLabel: 'Show',
              sizesDescription: 'per page',
            }}
            sorting={[{ id: 'id', desc: true }]}
            serverSide={true}
            aria-label="Version files table"
            messages={{
              empty: intl.formatMessage({ id: 'ROUTING.NO_VERSION_FILES' }),
            }}
          />
        )}
      </div>
    </div>
  );
};
